import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

export interface TrialTimelineProps {
  /**
   * Trial length in days, always sourced from the server-driven paywall
   * config (`PaywallConfig.plans[].trial_days`) — NEVER hardcode this value.
   */
  trialDays: number;
  /** How many days into the trial the user currently is (0-indexed). Optional — omit to render the generic timeline. */
  currentDay?: number;
}

interface TimelineStep {
  key: string;
  dayLabel: string;
  description: string;
  day: number;
}

/**
 * Honest-paywall trial timeline: D-0 (trial starts) -> D-3 (reminder email)
 * -> D-{trialDays} (trial ends). Fully parametrized by `trialDays` — the
 * milestone-13 spec (Q1) fixes this at 7 for v1, but this component must
 * never assume that value so it keeps working if the config ever changes.
 */
export const TrialTimeline: React.FC<TrialTimelineProps> = ({ trialDays, currentDay }) => {
  const { t } = useTranslation('paywall');
  const theme = useTheme();

  // "D-3" reminder = 3 days BEFORE the charge, not day 3 of the trial:
  // day 4 on a 7-day trial, day 11 on a 14-day one (#209 spec / Q1).
  const reminderDay = Math.max(1, trialDays - 3);

  const steps: TimelineStep[] = [
    {
      key: 'day0',
      dayLabel: t('timeline.day0Label'),
      description: t('timeline.day0Description'),
      day: 0,
    },
    {
      key: 'reminder',
      dayLabel: t('timeline.reminderLabel', { day: reminderDay }),
      description: t('timeline.reminderDescription'),
      day: reminderDay,
    },
    {
      key: 'trialEnd',
      dayLabel: t('timeline.trialEndLabel', { days: trialDays }),
      description: t('timeline.trialEndDescription'),
      day: trialDays,
    },
  ];

  return (
    <View style={styles.container} testID="trial-timeline">
      <Text variant="titleMedium" style={[styles.title, { color: theme.colors.primary }]}>
        {t('timeline.title')}
      </Text>
      <View style={styles.stepsRow}>
        {steps.map((step, index) => {
          const isPast = currentDay !== undefined && currentDay >= step.day;
          const dotColor = isPast ? theme.colors.primary : theme.colors.outline;
          return (
            <React.Fragment key={step.key}>
              <View style={styles.step} testID={`trial-timeline-step-${step.key}`}>
                <View style={[styles.dot, { backgroundColor: dotColor }]} />
                <Text variant="labelMedium" style={{ color: theme.colors.onSurface }}>
                  {step.dayLabel}
                </Text>
                <Text
                  variant="bodySmall"
                  style={[styles.description, { color: theme.colors.onSurfaceVariant }]}
                >
                  {step.description}
                </Text>
              </View>
              {index < steps.length - 1 && (
                <View style={[styles.connector, { backgroundColor: theme.colors.outline }]} />
              )}
            </React.Fragment>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  title: {
    marginBottom: 12,
  },
  stepsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  step: {
    flex: 1,
    alignItems: 'center',
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginBottom: 6,
  },
  description: {
    textAlign: 'center',
    marginTop: 2,
  },
  connector: {
    height: 2,
    flex: 0.5,
    marginTop: 6,
  },
});

export default TrialTimeline;
